import CalendarViewDayOutlinedIcon from "@mui/icons-material/CalendarViewDayOutlined";
import CalendarViewMonthOutlinedIcon from "@mui/icons-material/CalendarViewMonthOutlined";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import DateRangeOutlinedIcon from "@mui/icons-material/DateRangeOutlined";
import ViewWeekOutlinedIcon from "@mui/icons-material/ViewWeekOutlined";
import { Box, Button, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { addMonths, formatDate, makeCalendarDays, makeCalendarRange } from "../../utils/calendarUtils";
import { addDays, isSameDate, overlapsRange } from "../../utils/timelineUtils";

const modeOptions = [
  { key: "year", label: "연", icon: <DateRangeOutlinedIcon fontSize="small" /> },
  { key: "month", label: "월", icon: <CalendarViewMonthOutlinedIcon fontSize="small" /> },
  { key: "week", label: "주", icon: <ViewWeekOutlinedIcon fontSize="small" /> },
  { key: "day", label: "일", icon: <CalendarViewDayOutlinedIcon fontSize="small" /> },
];

const dayStart = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const startOfWeek = (date) => {
  const start = dayStart(date);
  start.setDate(start.getDate() - start.getDay());
  return start;
};

const endOfWeek = (date) => addDays(startOfWeek(date), 6);

const getVisibleLimit = (mode) => {
  if (mode === "day") return 10;
  if (mode === "week") return 8;
  return 4;
};

const getCalendarWeeks = (days = [], mode) => {
  if (mode === "day") return [days];
  return Array.from({ length: Math.ceil(days.length / 7) }, (_, index) => days.slice(index * 7, index * 7 + 7));
};

const getRangeByMode = (mode, anchorDate) => {
  const anchor = dayStart(anchorDate);
  if (mode === "day") return { start: anchor, end: anchor, title: `${anchor.getFullYear()}년 ${anchor.getMonth() + 1}월 ${anchor.getDate()}일` };
  if (mode === "week") {
    const start = startOfWeek(anchor);
    const end = endOfWeek(anchor);
    return { start, end, title: `${start.getMonth() + 1}/${start.getDate()} - ${end.getMonth() + 1}/${end.getDate()}` };
  }
  if (mode === "year") {
    const start = new Date(anchor.getFullYear(), 0, 1);
    const end = new Date(anchor.getFullYear(), 11, 31);
    return { start, end, title: `${anchor.getFullYear()}년` };
  }
  const range = makeCalendarRange(anchor);
  return { ...range, title: `${anchor.getFullYear()}년 ${anchor.getMonth() + 1}월` };
};

const moveDate = (date, mode, direction) => {
  if (mode === "year") return new Date(date.getFullYear() + direction, date.getMonth(), 1);
  if (mode === "month") return addMonths(date, direction);
  if (mode === "week") return addDays(date, direction * 7);
  return addDays(date, direction);
};

const getSegment = (row, periodDays) => {
  const periodStart = periodDays[0];
  const periodEnd = periodDays[periodDays.length - 1];
  if (!overlapsRange(row, periodStart, periodEnd)) return null;

  const segmentStart = row.startDate > periodStart ? row.startDate : periodStart;
  const segmentEnd = row.dueDate < periodEnd ? row.dueDate : periodEnd;
  const startIndex = periodDays.findIndex((day) => isSameDate(day, segmentStart));
  const endIndex = periodDays.findIndex((day) => isSameDate(day, segmentEnd));

  return {
    startColumn: (startIndex >= 0 ? startIndex : 0) + 1,
    endColumn: (endIndex >= 0 ? endIndex : periodDays.length - 1) + 2,
    startsInRange: isSameDate(segmentStart, row.startDate),
    endsInRange: isSameDate(segmentEnd, row.dueDate),
  };
};

const getSegmentLabel = (segment, row) => {
  if (segment.startsInRange) return `착수 · ${row.orderNo}`;
  if (segment.endsInRange) return `납기 · ${row.orderNo}`;
  return row.orderNo;
};

const CalendarTooltipContent = ({ row }) => (
  <Box sx={{ minWidth: 250, maxWidth: 320 }}>
    <Typography sx={{ mb: 1, fontSize: 13.5, fontWeight: 900, lineHeight: 1.35, color: "#fff" }}>
      {row.orderNo || "-"} · {row.itemName || "품목명 없음"}
    </Typography>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "56px minmax(0, 1fr)",
        gap: "6px 10px",
        fontSize: 12.5,
        lineHeight: 1.35,
      }}
    >
      {[
        ["거래처", row.customerName],
        ["품목", row.itemName],
        ["착수", row.startLabel],
        ["납기", row.dueLabel],
        ["여유일", row.availableMarginDays === null || row.availableMarginDays === undefined ? "-" : `${row.availableMarginDays}일`],
        ["일정", row.scheduleLabel],
        ["위험", row.riskLabel],
      ].map(([label, value]) => (
        <Box key={label} sx={{ display: "contents" }}>
          <Box component="span" sx={{ color: "#cbd5e1", fontWeight: 600 }}>
            {label}
          </Box>
          <Box component="strong" sx={{ minWidth: 0, color: "#fff", fontWeight: 800, wordBreak: "keep-all" }}>
            {value || "-"}
          </Box>
        </Box>
      ))}
    </Box>
  </Box>
);

const calendarTooltipProps = {
  arrow: true,
  placement: "top",
  enterDelay: 120,
  componentsProps: {
    tooltip: {
      sx: {
        p: 1.5,
        borderRadius: "8px",
        bgcolor: "#111827",
        boxShadow: "0 12px 28px rgba(15, 23, 42, 0.22)",
      },
    },
    arrow: { sx: { color: "#111827" } },
  },
};

const YearOverview = ({ rows, anchorDate, onSelectMonth, onOpenOrder }) => {
  const today = dayStart(new Date());
  const months = Array.from({ length: 12 }, (_, month) => {
    const start = new Date(anchorDate.getFullYear(), month, 1);
    const end = new Date(anchorDate.getFullYear(), month + 1, 0);
    const items = rows.filter((row) => overlapsRange(row, start, end));
    const dueItems = rows.filter((row) => row.dueDate >= start && row.dueDate <= end);
    return { month, start, end, items, dueItems };
  });

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "grid",
        gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" },
        gap: 1,
        overflow: "hidden",
      }}
    >
      {months.map((item) => {
        const isThisMonth = today.getFullYear() === anchorDate.getFullYear() && today.getMonth() === item.month;
        return (
          <Box
            key={item.month}
            component="button"
            type="button"
            onClick={() => onSelectMonth(item.start)}
            sx={{
              p: 1.25,
              border: `1px solid ${isThisMonth ? "#6c5ce7" : "#e5e7eb"}`,
              borderRadius: "8px",
              bgcolor: isThisMonth ? "#f5f3ff" : "#fff",
              textAlign: "left",
              cursor: "pointer",
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography sx={{ fontSize: 15, fontWeight: 900, color: "#111827" }}>{item.month + 1}월</Typography>
              {isThisMonth && (
                <Typography sx={{ px: 0.75, py: 0.2, borderRadius: 999, bgcolor: "#6c5ce7", color: "#fff", fontSize: 11, fontWeight: 900 }}>
                  오늘
                </Typography>
              )}
            </Stack>
            <Stack direction="row" spacing={0.8} sx={{ mt: 0.8 }}>
              <Typography sx={{ fontSize: 12.3, color: "#64748b" }}>일정 {item.items.length}건</Typography>
              <Typography sx={{ fontSize: 12.3, color: "#64748b" }}>납기 {item.dueItems.length}건</Typography>
            </Stack>
            <Stack spacing={0.45} sx={{ mt: 0.9 }}>
              {item.items.slice(0, 2).map((row) => (
                <Box
                  key={`${item.month}-${row.id}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    onOpenOrder?.(row.orderId);
                  }}
                  sx={{
                    px: 0.8,
                    py: 0.45,
                    borderRadius: "6px",
                    bgcolor: row.riskBg,
                    color: row.riskTone,
                    fontSize: 11.5,
                    fontWeight: 800,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.orderNo} · {row.itemName}
                </Box>
              ))}
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
};

export const OssDashboardConnectedCalendar = ({ rows = [], onOpenOrder }) => {
  const [viewMode, setViewMode] = useState("month");
  const [anchorDate, setAnchorDate] = useState(() => dayStart(new Date()));
  const today = useMemo(() => dayStart(new Date()), []);
  const range = useMemo(() => getRangeByMode(viewMode, anchorDate), [viewMode, anchorDate]);
  const periodDays = useMemo(() => {
    if (viewMode === "year") return [];
    return makeCalendarDays(range.start, range.end);
  }, [range.start, range.end, viewMode]);
  const weeks = useMemo(() => getCalendarWeeks(periodDays, viewMode), [periodDays, viewMode]);
  const visibleLimit = getVisibleLimit(viewMode);
  const visibleRows = useMemo(
    () => rows.filter((row) => viewMode !== "year" && overlapsRange(row, range.start, range.end)).slice(0, visibleLimit),
    [rows, range.start, range.end, viewMode, visibleLimit]
  );

  const columns = viewMode === "day" ? 1 : 7;

  return (
    <Stack spacing={1.1} sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
      <Stack direction={{ xs: "column", md: "row" }} alignItems={{ xs: "stretch", md: "center" }} justifyContent="space-between" spacing={1} sx={{ flexShrink: 0 }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          {modeOptions.map((option) => (
            <Button
              key={option.key}
              size="small"
              variant={viewMode === option.key ? "contained" : "outlined"}
              startIcon={option.icon}
              onClick={() => setViewMode(option.key)}
              sx={{ minWidth: 70, height: 34 }}
            >
              {option.label}
            </Button>
          ))}
        </Stack>
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.75}>
          <IconButton size="small" onClick={() => setAnchorDate((prev) => moveDate(prev, viewMode, -1))} sx={{ width: 34, height: 34, color: "#64748b" }}>
            <ChevronLeftRoundedIcon />
          </IconButton>
          <Button
            variant="text"
            onClick={() => setAnchorDate(dayStart(new Date()))}
            sx={{
              minWidth: 170,
              color: "#111827",
              fontSize: "17px !important",
              fontWeight: "900 !important",
              "&:hover": { bgcolor: "#f8fafc" },
            }}
          >
            {range.title}
          </Button>
          <IconButton size="small" onClick={() => setAnchorDate((prev) => moveDate(prev, viewMode, 1))} sx={{ width: 34, height: 34, color: "#64748b" }}>
            <ChevronRightRoundedIcon />
          </IconButton>
          <Button size="small" variant="outlined" onClick={() => setAnchorDate(dayStart(new Date()))} sx={{ height: 34 }}>
            오늘
          </Button>
        </Stack>
      </Stack>

      {viewMode === "year" ? (
        <YearOverview
          rows={rows}
          anchorDate={anchorDate}
          onOpenOrder={onOpenOrder}
          onSelectMonth={(date) => {
            setAnchorDate(date);
            setViewMode("month");
          }}
        />
      ) : (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            bgcolor: "#fff",
          }}
        >
          <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, borderBottom: "1px solid #e5e7eb" }}>
            {(viewMode === "day" ? ["일정"] : ["일", "월", "화", "수", "목", "금", "토"]).map((day) => (
              <Box key={day} sx={{ p: 0.8, textAlign: "center", bgcolor: "#f8fafc", fontSize: 12.5, fontWeight: 800, color: "#475569" }}>
                {day}
              </Box>
            ))}
          </Box>
          <Box sx={{ position: "relative", height: "calc(100% - 35px)", overflow: "hidden" }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                gridTemplateRows: `repeat(${weeks.length}, minmax(0, 1fr))`,
                height: "100%",
              }}
            >
              {periodDays.map((day) => {
                const dayKey = formatDate(day);
                const inMonth = day.getMonth() === anchorDate.getMonth() || viewMode !== "month";
                const isToday = isSameDate(day, today);
                return (
                  <Box
                    key={dayKey}
                    sx={{
                      minHeight: 0,
                      p: 0.6,
                      borderRight: "1px solid #e5e7eb",
                      borderBottom: "1px solid #e5e7eb",
                      bgcolor: isToday ? "#f5f3ff" : inMonth ? "#fff" : "#f8fafc",
                      opacity: inMonth ? 1 : 0.55,
                      overflow: "hidden",
                      boxShadow: isToday ? "inset 0 0 0 2px rgba(108, 92, 231, 0.28)" : "none",
                    }}
                  >
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography sx={{ fontSize: 12.5, fontWeight: 900, color: isToday ? "#6c5ce7" : "#111827" }}>
                        {viewMode === "day" ? `${day.getMonth() + 1}/${day.getDate()}` : day.getDate()}
                      </Typography>
                      {isToday && (
                        <Typography sx={{ px: 0.55, py: 0.1, borderRadius: 999, bgcolor: "#6c5ce7", color: "#fff", fontSize: 10.5, fontWeight: 900 }}>
                          오늘
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                );
              })}
            </Box>
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "grid",
                gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                gridTemplateRows: `repeat(${weeks.length}, minmax(0, 1fr))`,
                pointerEvents: "none",
              }}
            >
              {weeks.flatMap((weekDays, weekIndex) =>
                visibleRows.map((row, rowIndex) => {
                  const segment = getSegment(row, weekDays);
                  if (!segment) return null;
                  const laneTop = 29 + rowIndex * 20;
                  return (
                    <Tooltip
                      key={`${row.id}-${weekIndex}`}
                      title={<CalendarTooltipContent row={row} />}
                      {...calendarTooltipProps}
                    >
                      <Button
                        onClick={() => onOpenOrder?.(row.orderId)}
                        sx={{
                          gridColumn: `${segment.startColumn} / ${segment.endColumn}`,
                          gridRow: `${weekIndex + 1}`,
                          alignSelf: "start",
                          justifySelf: "stretch",
                          mt: `${laneTop}px`,
                          ml: segment.startsInRange ? "6px" : 0,
                          mr: segment.endsInRange ? "6px" : 0,
                          minWidth: 0,
                          minHeight: 18,
                          height: 18,
                          px: 0.6,
                          py: 0,
                          borderRadius: `${segment.startsInRange ? 999 : 0}px ${segment.endsInRange ? 999 : 0}px ${segment.endsInRange ? 999 : 0}px ${segment.startsInRange ? 999 : 0}px`,
                          bgcolor: row.riskBg,
                          color: row.riskTone,
                          border: `1px solid ${row.riskTone}30`,
                          boxShadow: `inset 0 0 0 1px ${row.riskTone}10`,
                          fontSize: "10.8px !important",
                          fontWeight: "800 !important",
                          lineHeight: 1,
                          overflow: "hidden",
                          textTransform: "none",
                          pointerEvents: "auto",
                          "&:hover": {
                            bgcolor: row.riskBg,
                            borderColor: `${row.riskTone}66`,
                            boxShadow: `0 2px 8px ${row.riskTone}22`,
                          },
                        }}
                      >
                        <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {getSegmentLabel(segment, row)}
                        </Box>
                      </Button>
                    </Tooltip>
                  );
                })
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Stack>
  );
};
